import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Rating,
  Typography,
  Stack,
  Alert,
  Backdrop,
  Modal,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import GlobalHeader from "../../../components/GlobalHeader";
import SnackAlert from "../../../components/SnackAlert";
import useSnack from "../../../hooks/useSnack";
import useApi from "../../../hooks/useApi";
import { useUser } from "../../../hooks/useUser";
import { gestionaMultiplesArtistas } from "../../../utils/tiposArtista";
import {
  formatearFecha,
  plazoEnPalabras,
} from "../../../utils/fechasEvento";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Audio from "react-loading-icons/dist/esm/components/audio";
import CircularProgress from "@mui/material/CircularProgress";
import { BorderColor } from "@mui/icons-material";
import { hrefSeguro } from "../../../utils/hrefSeguro";
import { urlsDelPost } from "../../../utils/urlsPost";
import { ESTILO_VENCIDO, etiquetaVencido } from "../../../utils/estadoEnvio";
import { LoadingButton } from "@mui/lab";

const REPORTE_MIN = 20;

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },

  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const styleModal = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  // En el celular, 400 px más el padding se salían de la pantalla.
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "90vh",
  overflowY: "auto",
  boxSizing: "border-box",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: { xs: 2.5, sm: 4 },
  borderRadius: "20px",
};

const TODOS = "todos";

export default function ViewArtistSends() {
  const [mainData, setMainData] = useState([]);
  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [dataModal, setDataModal] = useState({});
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const { loadApi, loadedApi, loadingApi } = useApi();
  const { user, updateUser } = useUser();

  // Los ecosistemas que mueven varios artistas ven columnas y filtros que a
  // un solista no le dicen nada: el perfil ES su único artista.
  const multiartista = gestionaMultiplesArtistas(user?.profile?.tipo);

  // La ficha de cada artista enlaza acá con ?artista=<id>, así que el filtro
  // arranca puesto en vez de obligar a buscarlo entre todo el historial.
  const [searchParams] = useSearchParams();
  const pedidoDestacado = searchParams.get("pedido");
  const vieneDeDirectLink = searchParams.get("direct") === "1";
  const [filtroArtista, setFiltroArtista] = useState(
    searchParams.get("artista") || TODOS
  );
  const [filtroTipo, setFiltroTipo] = useState(TODOS);

  useEffect(() => {
    getEnvios();
  }, []);

  useEffect(() => {
    if (!pedidoDestacado || mainData.length === 0) return;
    const target = window.document.getElementById(
      `envio-${pedidoDestacado}`
    );
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [pedidoDestacado, mainData]);

  const getEnvios = () => {
    loadApi("artista/envios", true, "get")
      .then((response) => {
        setMainData(
          response.data.map((data) => {
            return {
              // `titulo` unifica canción y evento; `cancion` sigue viniendo
              // null en los eventos.
              name: data.titulo || data.cancion,
              tipo: data.tipo_envio === "evento" ? "evento" : "cancion",
              fechaEvento: data.fecha_evento || null,
              artistaId: data.managed_artist_id || null,
              artista: data.artista_nombre || null,
              productor: data.productor,
              respuesta: data.status,
              calificado: data.calificado,
              productorId: data.productorId,
              id: data.id,
              feedback: data.feedback,
              urls_post: urlsDelPost(data),
              ajustes: data.ajustes_admin || [],
              reporte: data.reporte || null,
              servicioDirecto: data.direct_service_name || null,
              fechaEnvio: data.createdAt,
              horasLimite: data.horas_limite,
              radarCoin: data.radarCoin,
              planNombre: data.plan_nombre,
              plazoReenvio: data.plazo_reenvio,
              reenviado: data.reenviado,
              esReenvio: data.es_reenvio,
              puedeReenviar: data.puede_reenviar,
              motivoReenvio: data.motivo_reenvio,
            };
          }) || []
        );
      })
      .catch((e) => {
        showSnack(e.message, "error", false);
      });
  };

  // Los artistas que realmente tienen envíos: la lista sale del historial y
  // no del roster, para no ofrecer filtros que no devuelven nada.
  const artistasConEnvios = useMemo(() => {
    const porId = new Map();
    mainData.forEach((row) => {
      if (row.artistaId && !porId.has(row.artistaId))
        porId.set(row.artistaId, row.artista || `Artista #${row.artistaId}`);
    });
    return [...porId].map(([id, nombre]) => ({ id, nombre }));
  }, [mainData]);

  const filtrados = useMemo(
    () =>
      mainData.filter((row) => {
        if (filtroTipo !== TODOS && row.tipo !== filtroTipo) return false;
        if (filtroArtista === TODOS) return true;
        if (filtroArtista === "sin_artista") return !row.artistaId;
        return String(row.artistaId) === String(filtroArtista);
      }),
    [mainData, filtroArtista, filtroTipo]
  );

  // Resumen por artista, sobre el filtro de tipo pero no sobre el de artista:
  // la tabla muestra uno, el resumen sirve para comparar entre todos.
  const resumen = useMemo(() => {
    const porTipo = mainData.filter(
      (row) => filtroTipo === TODOS || row.tipo === filtroTipo
    );
    const filas = new Map();
    porTipo.forEach((row) => {
      const clave = row.artistaId || "sin_artista";
      const fila =
        filas.get(clave) ||
        filas
          .set(clave, {
            clave,
            nombre: row.artista || "Sin artista asignado",
            total: 0,
            aprobados: 0,
            rechazados: 0,
            vencidos: 0,
            pendientes: 0,
          })
          .get(clave);
      fila.total++;
      if (row.respuesta === "approve") fila.aprobados++;
      else if (row.respuesta === "rejected") fila.rechazados++;
      else if (row.respuesta === "vencido") fila.vencidos++;
      else if (row.respuesta === "pending") fila.pendientes++;
    });
    return [...filas.values()].sort((a, b) => b.total - a.total);
  }, [mainData, filtroTipo]);

  const handleRating = (data) => {
    setOpen(true);
    loadApi("artista/calificarProductor", true, "put", data)
      .then((res) => {
        showSnack(res.data.message);
        getEnvios();
        setOpen(false);
      })
      .catch((e) => {
        showSnack(e.message, "error");
        setOpen(false);
      });
  };

  // Volver a enviar un aprobado o reenviar un vencido (mismo difusor, mismo
  // tipo), y cancelar el reenvío de un vencido. Ver
  // Backend/src/utils/reenvios.js.
  const [reenviando, setReenviando] = useState(null); // fila
  const [cancelando, setCancelando] = useState(null); // fila

  const reenviar = () =>
    loadApi(`artista/envios/${reenviando.id}/reenviar`, true, "post", {})
      .then((res) => showSnack(res.data.message))
      .catch((e) => showSnack(e.message, "error"))
      .finally(() => {
        setReenviando(null);
        updateUser();
        getEnvios();
      });

  const cancelarReenvio = () =>
    loadApi(`artista/envios/${cancelando.id}/cancelar-reenvio`, true, "post", {})
      .then((res) => showSnack(res.data.message))
      .catch((e) => showSnack(e.message, "error"))
      .finally(() => {
        setCancelando(null);
        getEnvios();
      });

  const creditos = Number(user?.profile?.credits) || 0;

  // Reporte de un envío aprobado que el difusor no cumplió.
  const [reportando, setReportando] = useState(false);
  const [textoReporte, setTextoReporte] = useState("");

  const enviarReporte = () =>
    loadApi(`artista/envios/${dataModal.id}/reporte`, true, "post", {
      mensaje: textoReporte.trim(),
    })
      .then((res) => {
        showSnack(res.data.message);
        const { reporte } = res.data;
        setDataModal((prev) => ({ ...prev, reporte }));
        setMainData((prev) =>
          prev.map((e) => (e.id === dataModal.id ? { ...e, reporte } : e))
        );
        setReportando(false);
        setTextoReporte("");
      })
      .catch((e) => showSnack(e.message, "error"));

  const handleCloseModal = () => {
    setOpenModal(false);
    setReportando(false);
  };

  const handleOpenModal = (row) => {
    setDataModal(row);
    setOpenModal(true);
  };

  return (
    <>
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box sx={styleModal}>
          <Stack>
            {dataModal.respuesta === "approve" && (
              <Stack>
                <Typography textAlign="center">
                  {dataModal.urls_post?.length > 1
                    ? `Publicaciones del difusor (${dataModal.urls_post.length})`
                    : "Post recibido"}
                </Typography>
                <Box
                  sx={{
                    padding: "10px 10px 10px 20px",
                    borderRadius: "10px",
                    borderTopLeftRadius: "0",
                    backgroundColor: "#f1f1f1",
                    mt: 1,
                    "::after": {
                      content: "''",
                      position: "absolute",
                      left: "22px",
                      top: "54.5px",
                      border: "10px solid",
                      borderColor: "transparent transparent  #f1f1f1",
                    },
                  }}
                >
                  <Typography
                    sx={{ wordWrap: "break-word", textDecoration: "underline" }}
                    variant="subtitle1"
                    component="div"
                    color="GrayText"
                    fontWeight={400}
                  >
                    {(dataModal.urls_post || []).map((link) => (
                      <div key={link}>
                        <a href={hrefSeguro(link)} target="_blank" rel="nofollow noreferrer">
                          {link}
                        </a>
                      </div>
                    ))}
                  </Typography>
                </Box>
              </Stack>
            )}
            <Typography textAlign="center">Feedback recibido</Typography>
            <Box
              sx={{
                padding: "10px 10px 10px 20px",
                borderRadius: "10px",
                borderTopLeftRadius: "0",
                backgroundColor: "#f1f1f1",
                mt: 1,
                "::after": {
                  content: "''",
                  position: "absolute",
                  left: "22px",
                  top: "54.5px",
                  border: "10px solid",
                  borderColor: "transparent transparent  #f1f1f1",
                },
              }}
            >
              <Typography
                sx={{ wordWrap: "break-word" }}
                variant="subtitle1"
                color="GrayText"
                fontWeight={400}
              >
                {dataModal.feedback || "El difusor no anexó feedback"}
              </Typography>
            </Box>

            {/* Reporte: el difusor aprobó pero no cumplió con lo pedido */}
            {dataModal.reporte ? (
              <Alert
                sx={{ mt: 2 }}
                severity={
                  ["aprobado", "corregido"].includes(dataModal.reporte.estado)
                    ? "success"
                    : dataModal.reporte.estado === "rechazado"
                      ? "warning"
                      : "info"
                }
              >
                {dataModal.reporte.estado === "pendiente" &&
                  "Reportaste este envío: el equipo lo está revisando y te avisará."}
                {dataModal.reporte.estado === "correccion" &&
                  (dataModal.reporte.correccion?.estado === "entregada"
                    ? "El difusor entregó la corrección: el equipo la está revisando y te avisará."
                    : `Revisamos tu reporte: le pedimos al difusor que corrija el envío. Tiene hasta el ${fechaCorta(
                        dataModal.reporte.correccion?.vence
                      )}; si no lo corrige, se te devuelven ${
                        dataModal.reporte.correccion?.devolverRC
                      } RC.`)}
                {dataModal.reporte.estado === "corregido" && (
                  <>
                    Tu reporte procedió y el difusor corrigió el envío. Las
                    publicaciones nuevas:
                    {(dataModal.reporte.correccion?.urls || []).map((link) => (
                      <Box key={link} sx={{ overflowWrap: "anywhere" }}>
                        <a href={hrefSeguro(link)} target="_blank" rel="noopener noreferrer">
                          {link}
                        </a>
                      </Box>
                    ))}
                  </>
                )}
                {dataModal.reporte.estado === "aprobado" &&
                  `Tu reporte fue aprobado: se te devolvieron ${dataModal.reporte.devueltoRC} RC.`}
                {dataModal.reporte.estado === "rechazado" && (
                  <>
                    <strong>Revisamos tu reporte:</strong>{" "}
                    {dataModal.reporte.respuesta}
                  </>
                )}
              </Alert>
            ) : (
              dataModal.respuesta === "approve" &&
              (reportando ? (
                <Stack spacing={1} mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    Cuéntanos qué no se cumplió (por ejemplo: pediste una nota
                    editorial y solo hubo un post en redes). El equipo lo
                    revisa y, si procede, se te devuelven tus RC.
                  </Typography>
                  <TextField
                    multiline
                    minRows={3}
                    value={textoReporte}
                    onChange={(e) => setTextoReporte(e.target.value)}
                    inputProps={{ maxLength: 1000, "aria-label": "Qué no se cumplió" }}
                    helperText={`${textoReporte.trim().length}/1000 (mínimo ${REPORTE_MIN})`}
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <LoadingButton onClick={() => setReportando(false)}>
                      Cancelar
                    </LoadingButton>
                    <LoadingButton
                      variant="contained"
                      color="error"
                      disabled={textoReporte.trim().length < REPORTE_MIN}
                      loading={loadingApi.includes(
                        `post__artista/envios/${dataModal.id}/reporte`
                      )}
                      onClick={enviarReporte}
                    >
                      Enviar reporte
                    </LoadingButton>
                  </Stack>
                </Stack>
              ) : (
                <LoadingButton
                  sx={{ mt: 2, alignSelf: "center" }}
                  color="error"
                  onClick={() => setReportando(true)}
                >
                  Reportar que no se cumplió
                </LoadingButton>
              ))
            )}
          </Stack>
        </Box>
      </Modal>
      <Backdrop sx={{ color: "#fff", zIndex: 9999 }} open={open}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <GlobalHeader />
      {!loadedApi.includes("get__artista/envios") && mainData.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100vw",
            height: `calc(100vh - ${
              window.document.getElementById("GlobalHeader")?.offsetHeight ||
              "64"
            }px - 32px)`,
          }}
        >
          <Audio fill="#F27224" />
        </Box>
      ) : (
        <Container>
          <Grid container spacing={5}>
            <Grid container item spacing={5}>
              <Grid item xs={12}>
                <Typography variant="h4" color="primary.main" fontWeight="900">
                  Historial de Envios
                </Typography>
              </Grid>

              {vieneDeDirectLink && pedidoDestacado && (
                <Grid item xs={12}>
                  <Alert severity="success">
                    Tu pedido quedó vinculado a RadarFreim. Lo resaltamos abajo
                    para que puedas darle seguimiento desde aquí.
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <Stack direction="row" gap={2} flexWrap="wrap">
                  <TextField
                    select
                    size="small"
                    label="Tipo de envío"
                    value={filtroTipo}
                    onChange={(e) => setFiltroTipo(e.target.value)}
                    sx={{ minWidth: 200 }}
                  >
                    <MenuItem value={TODOS}>Todos</MenuItem>
                    <MenuItem value="cancion">Canciones</MenuItem>
                    <MenuItem value="evento">Eventos</MenuItem>
                  </TextField>

                  {multiartista && artistasConEnvios.length > 0 && (
                    <TextField
                      select
                      size="small"
                      label="Artista"
                      value={filtroArtista}
                      onChange={(e) => setFiltroArtista(e.target.value)}
                      sx={{ minWidth: 220 }}
                    >
                      <MenuItem value={TODOS}>Todos</MenuItem>
                      {artistasConEnvios.map((a) => (
                        <MenuItem key={a.id} value={String(a.id)}>
                          {a.nombre}
                        </MenuItem>
                      ))}
                      <MenuItem value="sin_artista">
                        Sin artista asignado
                      </MenuItem>
                    </TextField>
                  )}

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    alignSelf="center"
                  >
                    {filtrados.length} de {mainData.length} envíos
                  </Typography>
                </Stack>
              </Grid>

              {/* Resumen por artista: para un sello, saber cuál está teniendo
                  respuesta es la pregunta que trae a esta pantalla. */}
              {multiartista && resumen.length > 0 && (
                <Grid item xs={12}>
                  <TableContainer component={Paper}>
                    <Table size="small" aria-label="resumen por artista">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Artista</StyledTableCell>
                          <StyledTableCell align="center">
                            Envíos
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            Aprobados
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            Rechazados
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            Vencidos
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            Pendientes
                          </StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {resumen.map((fila) => (
                          <StyledTableRow key={fila.clave}>
                            <StyledTableCell component="th" scope="row">
                              {fila.nombre}
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              {fila.total}
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              {fila.aprobados}
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              {fila.rechazados}
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              {fila.vencidos}
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              {fila.pendientes}
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}

              <Grid item xs={12}>
                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 1000 }} aria-label="customized table">
                    {/* 1 seccion */}
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Enviado</StyledTableCell>
                        <StyledTableCell align="center">Tipo</StyledTableCell>
                        {multiartista && (
                          <StyledTableCell align="center">
                            Artista
                          </StyledTableCell>
                        )}
                        <StyledTableCell align="center">
                          Difusor
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          Respuesta
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          Calificación
                        </StyledTableCell>
                      </TableRow>
                    </TableHead>

                    {/* 2 seccion */}
                    <TableBody>
                      {filtrados.map((row, i) => {
                        const destacado =
                          pedidoDestacado &&
                          String(row.id) === String(pedidoDestacado);
                        return (
                        <StyledTableRow
                          key={row.id ?? i}
                          id={`envio-${row.id}`}
                          sx={
                            destacado
                              ? {
                                  bgcolor: "rgba(242, 114, 36, 0.16) !important",
                                  outline: "2px solid",
                                  outlineColor: "primary.main",
                                  outlineOffset: "-2px",
                                }
                              : undefined
                          }
                        >
                          <StyledTableCell component="th" scope="row">
                            {row.name}
                            {row.esReenvio && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                Reenvío
                              </Typography>
                            )}
                            {row.servicioDirecto && (
                              <Chip
                                label={row.servicioDirecto}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ ml: 1 }}
                              />
                            )}
                            {/* La fecha del evento es lo que ubica el envío:
                                sin ella, dos fechas del mismo tour se leen
                                iguales. */}
                            {row.tipo === "evento" && row.fechaEvento && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {formatearFecha(row.fechaEvento)}
                              </Typography>
                            )}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Chip
                              label={
                                row.tipo === "evento" ? "Evento" : "Canción"
                              }
                              size="small"
                              color={
                                row.tipo === "evento" ? "primary" : "default"
                              }
                            />
                          </StyledTableCell>
                          {multiartista && (
                            <StyledTableCell align="center">
                              {row.artista || "—"}
                            </StyledTableCell>
                          )}
                          <StyledTableCell align="center">
                            {row.productor}
                          </StyledTableCell>
                          {row.respuesta === "rejected" ? (
                            <StyledTableCell align="center">
                              <Chip
                                label="Rechazado"
                                color="error"
                                onClick={() => handleOpenModal(row)}
                              />
                            </StyledTableCell>
                          ) : row.respuesta === "approve" ? (
                            <StyledTableCell align="center">
                              <Stack alignItems="center" spacing={0.5}>
                                <Chip
                                  label="Aprobado"
                                  color="success"
                                  onClick={() => handleOpenModal(row)}
                                />
                                <AjusteDelEquipo ajustes={row.ajustes} />
                                <EstadoReporte reporte={row.reporte} />
                                {row.puedeReenviar && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setReenviando(row)}
                                  >
                                    Volver a enviar
                                  </Button>
                                )}
                              </Stack>
                            </StyledTableCell>
                          ) : row.respuesta === "vencido" ? (
                            <StyledTableCell align="center">
                              <EstadoVencido
                                row={row}
                                onReenviar={() => setReenviando(row)}
                                onCancelar={() => setCancelando(row)}
                              />
                            </StyledTableCell>
                          ) : row.respuesta === "anulado" ? (
                            <StyledTableCell align="center">
                              <Stack alignItems="center" spacing={0.5}>
                                <Chip label="Anulado" />
                                <AjusteDelEquipo ajustes={row.ajustes} />
                                <EstadoReporte reporte={row.reporte} />
                              </Stack>
                            </StyledTableCell>
                          ) : (
                            <StyledTableCell align="center">
                              <Stack alignItems="center" spacing={0.5}>
                                <Chip label="Pendiente" color="warning" />
                                <AjusteDelEquipo ajustes={row.ajustes} />
                                {row.fechaEnvio && row.horasLimite != null && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {plazoEnPalabras(
                                      row.fechaEnvio,
                                      row.horasLimite
                                    )}
                                  </Typography>
                                )}
                              </Stack>
                            </StyledTableCell>
                          )}
                          {row.calificado ? (
                            <StyledTableCell align="center">
                              <Typography variant="subtitle1" color="green">
                                CALIFICADO
                              </Typography>
                            </StyledTableCell>
                          ) : ["pending", "anulado", "vencido"].includes(row.respuesta) ? (
                            <StyledTableCell align="center">
                              <Typography color="green"> - </Typography>
                            </StyledTableCell>
                          ) : (
                            <StyledTableCell align="right">
                              <Stack
                                sx={{
                                  "& > legend": { mt: 2 },
                                  flexDirection: "row",
                                  justifyContent: "center",
                                }}
                              >
                                <Rating
                                  onChange={(event, newValue) => {
                                    handleRating({
                                      rating: newValue,
                                      productorId: row.productorId,
                                      envioId: row.id,
                                    });
                                  }}
                                />
                              </Stack>
                            </StyledTableCell>
                          )}
                        </StyledTableRow>
                        );
                      })}
                      {filtrados.length === 0 && (
                        <StyledTableRow>
                          <StyledTableCell colSpan={multiartista ? 6 : 5}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              textAlign="center"
                              py={2}
                            >
                              {mainData.length === 0
                                ? "Todavía no hiciste ningún envío."
                                : "Ningún envío coincide con el filtro."}
                            </Typography>
                          </StyledTableCell>
                        </StyledTableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      )}
      <Dialog open={Boolean(reenviando)} onClose={() => setReenviando(null)} maxWidth="xs" fullWidth>
        {reenviando && (
          <>
            <DialogTitle>
              {reenviando.respuesta === "vencido" ? "¿Reenviar" : "¿Volver a enviar"} «
              {reenviando.name}»?
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                Se le mandará otra vez a <strong>{reenviando.productor}</strong> como{" "}
                <strong>{reenviando.planNombre}</strong> y se descontarán{" "}
                <strong>{reenviando.radarCoin} RC</strong> (tienes {creditos} RC).
                {reenviando.respuesta === "vencido" &&
                  " Los RC de la vez anterior ya se te devolvieron."}
              </DialogContentText>
              {creditos < Number(reenviando.radarCoin) && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No te alcanzan los RC: puedes comprar más en la Tienda.
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setReenviando(null)}>Cancelar</Button>
              <LoadingButton
                variant="contained"
                disabled={creditos < Number(reenviando.radarCoin)}
                loading={loadingApi.includes(`post__artista/envios/${reenviando.id}/reenviar`)}
                onClick={reenviar}
              >
                Enviar
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={Boolean(cancelando)} onClose={() => setCancelando(null)} maxWidth="xs" fullWidth>
        {cancelando && (
          <>
            <DialogTitle>¿Cancelar el reenvío de «{cancelando.name}»?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Pasará a «Rechazado» y ya no se podrá volver a enviar a{" "}
                <strong>{cancelando.productor}</strong>. Tus RC ya se te devolvieron.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCancelando(null)}>Volver</Button>
              <LoadingButton
                color="error"
                variant="contained"
                loading={loadingApi.includes(
                  `post__artista/envios/${cancelando.id}/cancelar-reenvio`
                )}
                onClick={cancelarReenvio}
              >
                Cancelar reenvío
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
}

// Si el equipo corrigió el envío (lo pasó a Envío Social o lo anuló), qué
// pasó y cuántos RC se le devolvieron al artista.
function AjusteDelEquipo({ ajustes }) {
  const devuelto = (ajustes || []).reduce(
    (total, a) => total + (Number(a.devueltoRC) || 0),
    0
  );
  if (!ajustes?.length) return null;
  const ultimo = ajustes[ajustes.length - 1];
  return (
    <Typography variant="caption" color="text.secondary" textAlign="center">
      {ultimo.accion === "anular"
        ? "Anulado por el equipo"
        : "Pasado a Envío Social por el equipo"}
      {devuelto > 0 && ` · se te devolvieron ${devuelto} RC`}
    </Typography>
  );
}

// Un envío que el difusor no respondió a tiempo: los RC ya se devolvieron y
// el artista tiene unos días para reenviarlo o cancelar el reenvío. Ya
// reenviado, solo lo dice.
function EstadoVencido({ row, onReenviar, onCancelar }) {
  if (row.reenviado)
    return <Chip label={etiquetaVencido(true)} variant="outlined" />;
  return (
    <Stack alignItems="center" spacing={0.5}>
      <Chip label={etiquetaVencido(false)} sx={ESTILO_VENCIDO} />
      <Typography variant="caption" color="text.secondary" textAlign="center">
        No respondió a tiempo · se te devolvieron los RC
      </Typography>
      {row.motivoReenvio ? (
        <Typography variant="caption" color="text.secondary" textAlign="center">
          {row.motivoReenvio}
        </Typography>
      ) : (
        row.plazoReenvio && (
          <Typography variant="caption" fontWeight={600} textAlign="center">
            Para decidir: {plazoEnPalabras(row.plazoReenvio, 0)}
          </Typography>
        )
      )}
      <Stack direction="row" gap={1} flexWrap="wrap" justifyContent="center">
        {!row.motivoReenvio && (
          <Button size="small" variant="contained" onClick={onReenviar}>
            Reenviar
          </Button>
        )}
        <Button size="small" color="inherit" onClick={onCancelar}>
          Cancelar reenvío
        </Button>
      </Stack>
    </Stack>
  );
}

// El estado del reporte del artista, debajo del estado del envío.
function EstadoReporte({ reporte }) {
  if (!reporte) return null;
  const textos = {
    pendiente: "Reporte en revisión",
    correccion: "Reporte: el difusor está corrigiendo",
    aprobado: "Reporte aprobado",
    corregido: "Reporte: el difusor corrigió",
    rechazado: "Reporte: no procedió",
  };
  return (
    <Typography
      variant="caption"
      color={
        ["aprobado", "corregido"].includes(reporte.estado)
          ? "success.main"
          : "text.secondary"
      }
      textAlign="center"
    >
      {textos[reporte.estado] || ""}
    </Typography>
  );
}

// "19 de octubre, 11:03", para el plazo de una corrección.
function fechaCorta(iso) {
  return iso
    ? new Date(iso).toLocaleString("es", {
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
}
